import { isAllowedAttachmentUrl } from './attachment-url';
import { type ReportChatMessageFile } from './interfaces';

/**
 * One attachment on a chat message as it travels over the socket. Field names
 * are the backend contract (`attachments: [{url, filename}]`) — the proxy
 * stream is written against them, so they cannot be renamed client-side.
 */
export interface ReportChatAttachment {
  /** Absolute storage URL of the uploaded file. */
  url: string;
  /** Original client filename — the chip label and the image-vs-file signal. */
  filename: string;
}

/**
 * Extensions the thread previews inline. The storage URL carries only an ASCII
 * slug and serves `Content-Disposition: attachment`, so the original filename is
 * the only reliable type hint we get on the wire.
 *
 * `.svg` не в списке намеренно: SVG — документ, а не картинка, и при прямом
 * открытии исполняет скрипты. Превью ему не положено, чипом-файлом он остаётся.
 */
const IMAGE_EXTENSION = /\.(png|jpe?g|gif|webp|bmp|avif|heic|heif)(\?|#|$)/i;

function isImage(attachment: ReportChatAttachment): boolean {
  return IMAGE_EXTENSION.test(attachment.filename) || IMAGE_EXTENSION.test(attachment.url);
}

/**
 * Вложения, чей URL прошёл проверку {@link isAllowedAttachmentUrl}. Единственный
 * фильтр на исходящем пути: то, что мы отказываемся рисовать, мы и не шлём.
 */
export function allowedAttachments(attachments: ReportChatAttachment[] | undefined): ReportChatAttachment[] {
  return (attachments ?? []).filter(attachment => isAllowedAttachmentUrl(attachment.url));
}

/**
 * Wire attachments → renderable message files: images carry `previewUrl` (inline
 * thumbnail + lightbox), everything else falls back to the paperclip chip that
 * opens the file in a new tab. Shared by both viewers — the student modal and the
 * operator cabinet render the same rows off the same socket mapping.
 *
 * URL приходит с провода и уходит в `bypassSecurityTrustUrl` и `[href]`, поэтому
 * проверяется здесь, на входе: не прошедший проверку не попадает ни в `url`, ни
 * в `previewUrl` — остаётся чип с одной меткой. Честнее битой ссылки и не даёт
 * чужому URL молча оказаться в DOM.
 */
export function toMessageFiles(attachments: ReportChatAttachment[] | undefined): ReportChatMessageFile[] | undefined {
  if (!attachments?.length) {
    return undefined;
  }
  return attachments.map(attachment => {
    const url = isAllowedAttachmentUrl(attachment.url) ? attachment.url : undefined;
    return {
      label: attachment.filename,
      ...(url ? { url } : {}),
      ...(url && isImage(attachment) ? { previewUrl: url } : {}),
    };
  });
}

/**
 * Renderable message files → wire attachments. Used to re-send the attachment
 * list of a failed optimistic row on retry: the row itself is the only place the
 * list survives, and `label`/`url` are written from `filename`/`url` above.
 */
export function toAttachments(files: ReportChatMessageFile[] | undefined): ReportChatAttachment[] {
  return (files ?? [])
    .filter((file): file is ReportChatMessageFile & { url: string } => isAllowedAttachmentUrl(file.url))
    .map(file => ({ url: file.url, filename: file.label }));
}
